import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { format, isPast, parseISO } from 'date-fns';
import { AlertCircle, CheckCircle, Clock, Shield, AlertTriangle, Edit, X, Eye, Save, ShieldAlert, FileText, ExternalLink } from 'lucide-react';
import MultiUserSelect from '../components/MultiUserSelect';
import { ANNEX_A } from './Controls.jsx';
import { RiskModal } from './Risks.jsx';
import { useNavigate } from 'react-router-dom';

// ── Constants ────────────────────────────────────────────────────────────────
const CONTROL_TYPES = ['Preventive', 'Detective', 'Corrective'];
const CATEGORIES = ['Administrative', 'Technical', 'Physical'];
const CTRL_STATUSES = ['Not Started', 'In Progress', 'Implemented', 'Planned'];
const RISK_STATUSES = ['Open', 'In Progress', 'Mitigated', 'Accepted', 'Closed'];
const TREATMENT_TYPES = ['Mitigate', 'Transfer', 'Accept', 'Avoid'];
const ALL_ANNEX = ANNEX_A.flatMap(d => d.controls.map(c => ({ id: c.id, label: `${c.id} - ${c.name}` })));

function normalizeDate(val) {
  if (!val || typeof val !== 'string') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (val.includes('T')) return val.split('T')[0];
  if (/^\d{4}-\d{2}-\d{2} /.test(val)) return val.split(' ')[0];
  const d = new Date(val);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return '';
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const safeFormat = (dateStr, fmt = 'dd MMM yyyy') => {
  if (!dateStr) return 'No date';
  try { const d = parseISO(dateStr); return isNaN(d.getTime()) ? dateStr : format(d, fmt); }
  catch { return dateStr; }
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  try { const d = parseISO(dateStr); return !isNaN(d.getTime()) && isPast(d); }
  catch { return false; }
};

const badgeForRisk = (status) =>
  ({ Open: 'badge-danger', 'In Progress': 'badge-warning', Mitigated: 'badge-success', Accepted: 'badge-info', Closed: 'badge-neutral' }[status] || 'badge-neutral');

const badgeForCtrl = (status) =>
  ({ Implemented: 'badge-success', 'In Progress': 'badge-warning', 'Not Started': 'badge-danger', Planned: 'badge-info' }[status] || 'badge-neutral');

const badgeForVuln = (status) =>
  ({ Open: 'badge-danger', 'In Progress': 'badge-warning', Resolved: 'badge-success', Closed: 'badge-neutral' }[status] || 'badge-neutral');

// ── Main Component ────────────────────────────────────────────────────────────
export function MyWork({ user }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All Pending'); // 'All Pending', 'All', 'Risk', 'Control', 'Vulnerability', 'Documents'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState('all');

  const { data: users = [] } = useQuery({ 
    queryKey: ['users'], 
    queryFn: api.getUsers 
  });

  const { data, isLoading } = useQuery({ 
    queryKey: ['my-work', selectedUser], 
    queryFn: () => api.getMyWork(selectedUser) 
  });
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets });
  const { data: securityControls = [] } = useQuery({ queryKey: ['security-controls'], queryFn: api.getSecurityControls });

  const updateRisk = useMutation({
    mutationFn: (v) => api.updateRisk(v.id, v.data),
    onSuccess: () => { queryClient.invalidateQueries(['my-work']); setEditItem(null); }
  });
  const updateCtrl = useMutation({
    mutationFn: (v) => api.updateSecurityControl(v.id, v.data),
    onSuccess: () => { queryClient.invalidateQueries(['my-work']); setEditItem(null); }
  });

  if (isLoading) return <div className="page"><div className="empty-state">Loading your assignments...</div></div>;

  const { risks = [], controls = [], vulnerabilities = [], documents = [] } = data || {};

  // Status definitions
  const activeRiskStatuses = ['Open', 'In Progress'];
  const completedRiskStatuses = ['Mitigated', 'Accepted', 'Closed'];
  
  const activeCtrlStatuses = ['Not Started', 'In Progress', 'Planned'];
  const completedCtrlStatuses = ['Implemented'];

  const activeVulnStatuses = ['Open', 'In Progress'];
  const completedVulnStatuses = ['Resolved', 'Closed'];

  // Metrics helper
  const getMetrics = (items, activeStatuses, completedStatuses) => {
    const total = items.length;
    const pending = items.filter(i => activeStatuses.includes(i.status || i.implementation_status)).length;
    const completed = items.filter(i => completedStatuses.includes(i.status || i.implementation_status)).length;
    const overdue = items.filter(i => i.due_date && isOverdue(i.due_date) && activeStatuses.includes(i.status || i.implementation_status)).length;
    return { total, pending, completed, overdue };
  };

  const riskMetrics = getMetrics(risks, activeRiskStatuses, completedRiskStatuses);
  const ctrlMetrics = getMetrics(controls, activeCtrlStatuses, completedCtrlStatuses);
  const vulnMetrics = getMetrics(vulnerabilities, activeVulnStatuses, completedVulnStatuses);
  const docMetrics = { total: documents.length, pending: documents.length, completed: 0, overdue: 0 }; 

  // Unified items
  const unifiedItems = [
    ...documents.map(d => ({
      _type: 'Document Review',
      _id: `DOC-${d.id}`,
      _realId: d.id,
      _title: `${d.title} (v${d.version})`,
      _dueDate: d.submitted_at || null,
      _status: 'Pending Review',
      _priority: d.category || 'Review',
      _priorityBadge: 'badge-neutral',
      _statusBadge: 'badge-warning',
      _raw: d,
      _sortWeight: 4 // Highest
    })),
    ...risks.map(r => ({
      _type: 'Risk',
      _id: r.custom_id,
      _realId: r.id,
      _title: r.description,
      _dueDate: r.due_date,
      _status: r.status,
      _priority: `Score: ${r.risk_score}`,
      _priorityBadge: r.risk_score >= 15 ? 'badge-danger' : r.risk_score >= 10 ? 'badge-warning' : 'badge-info',
      _statusBadge: badgeForRisk(r.status),
      _raw: r,
      _sortWeight: activeRiskStatuses.includes(r.status) ? 2 : 1
    })),
    ...vulnerabilities.map(v => ({
      _type: 'Vulnerability',
      _id: v.custom_id,
      _realId: v.id,
      _title: v.name,
      _dueDate: v.due_date,
      _status: v.status,
      _priority: v.severity,
      _priorityBadge: v.severity === 'Critical' ? 'badge-danger' : v.severity === 'High' ? 'badge-warning' : v.severity === 'Medium' ? 'badge-info' : 'badge-neutral',
      _statusBadge: badgeForVuln(v.status),
      _raw: v,
      _sortWeight: activeVulnStatuses.includes(v.status) ? 2 : 1
    })),
    ...controls.map(c => ({
      _type: 'Control',
      _id: c.control_id,
      _realId: c.id,
      _title: c.name,
      _dueDate: c.due_date,
      _status: c.implementation_status,
      _priority: c.control_type || 'Control',
      _priorityBadge: 'badge-neutral',
      _statusBadge: badgeForCtrl(c.implementation_status),
      _raw: c,
      _sortWeight: activeCtrlStatuses.includes(c.implementation_status) ? 2 : 1
    }))
  ];

  unifiedItems.sort((a, b) => {
    if (a._type === 'Document Review' && b._type !== 'Document Review') return -1;
    if (a._type !== 'Document Review' && b._type === 'Document Review') return 1;

    const aOverdue = a._dueDate && isOverdue(a._dueDate) && a._sortWeight === 2;
    const bOverdue = b._dueDate && isOverdue(b._dueDate) && b._sortWeight === 2;
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    if (a._sortWeight !== b._sortWeight) return b._sortWeight - a._sortWeight;

    if (a._dueDate && b._dueDate) return new Date(a._dueDate) - new Date(b._dueDate);
    if (a._dueDate) return -1;
    if (b._dueDate) return 1;

    return 0;
  });

  const filteredItems = unifiedItems.filter(item => {
    if (filter === 'All Pending') return item._sortWeight > 1 || item._type === 'Document Review';
    if (filter === 'All') return true;
    return item._type === filter || (filter === 'Documents' && item._type === 'Document Review');
  });

  let sortedFilteredItems = [...filteredItems];
  if (sortConfig.key) {
    sortedFilteredItems.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === '_dueDate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (sortConfig.key === '_typeAndId') {
        aVal = `${a._type} ${a._id}`;
        bVal = `${b._type} ${b._id}`;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <span style={{ opacity: 0.2, marginLeft: 4, fontSize: '0.65rem' }}>↕</span>;
    return <span style={{ marginLeft: 4, color: 'var(--accent)', fontSize: '0.65rem' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>My Work Dashboard</h1>
          <p>Manage your assigned risks, controls, vulnerabilities, and document reviews.</p>
        </div>
        {user?.role === 'Admin' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Viewing:</span>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
                padding: '8px 12px',
                borderRadius: '6px',
                minWidth: '200px'
              }}
            >
              <option value="all">All Users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { title: 'Assigned Risks', icon: <AlertTriangle size={24} style={{ color: 'var(--warning)' }} />, metrics: riskMetrics, color: 'var(--warning)', bg: 'var(--warning-dim, rgba(234, 179, 8, 0.1))' },
          { title: 'Assigned Controls', icon: <CheckCircle size={24} style={{ color: 'var(--accent)' }} />, metrics: ctrlMetrics, color: 'var(--accent)', bg: 'var(--accent-dim, rgba(16, 185, 129, 0.1))' },
          { title: 'Vulnerabilities', icon: <ShieldAlert size={24} style={{ color: 'var(--danger)' }} />, metrics: vulnMetrics, color: 'var(--danger)', bg: 'var(--danger-dim, rgba(239, 68, 68, 0.1))' },
          { title: 'Document Reviews', icon: <FileText size={24} style={{ color: 'var(--info)' }} />, metrics: docMetrics, color: 'var(--info)', bg: 'var(--info-dim, rgba(59, 130, 246, 0.1))' }
        ].map(card => (
          <div key={card.title} style={{ background: 'var(--bg-elevated)', border: `1px solid var(--border)`, borderRadius: 12, padding: '20px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ background: card.bg, padding: 12, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{card.icon}</div>
              <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 600 }}>{card.title}</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: card.metrics.total > 0 ? 'var(--bg-elevated)' : 'var(--bg-base)', border: '1px solid var(--border)', padding: '10px 12px', borderRadius: 8 }}>
                <div style={{ fontSize: '0.65rem', color: card.metrics.total > 0 ? 'var(--text-secondary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Total Assigned</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: card.metrics.total > 0 ? 'var(--text-primary)' : 'inherit' }}>{card.metrics.total}</div>
              </div>
              <div style={{ background: card.metrics.pending > 0 ? 'var(--info-dim, rgba(59, 130, 246, 0.1))' : 'var(--bg-base)', padding: '10px 12px', borderRadius: 8 }}>
                <div style={{ fontSize: '0.65rem', color: card.metrics.pending > 0 ? 'var(--info)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Pending</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: card.metrics.pending > 0 ? 'var(--info)' : 'inherit' }}>{card.metrics.pending}</div>
              </div>
              <div style={{ background: card.metrics.overdue > 0 ? 'var(--danger-dim, rgba(239, 68, 68, 0.1))' : 'var(--bg-base)', padding: '10px 12px', borderRadius: 8 }}>
                <div style={{ fontSize: '0.65rem', color: card.metrics.overdue > 0 ? 'var(--danger)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Overdue</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: card.metrics.overdue > 0 ? 'var(--danger)' : 'inherit' }}>{card.metrics.overdue}</div>
              </div>
              <div style={{ background: card.metrics.completed > 0 ? 'var(--success-dim, rgba(16, 185, 129, 0.1))' : 'var(--bg-base)', padding: '10px 12px', borderRadius: 8 }}>
                <div style={{ fontSize: '0.65rem', color: card.metrics.completed > 0 ? 'var(--success)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Completed</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: card.metrics.completed > 0 ? 'var(--success)' : 'inherit' }}>{card.metrics.completed}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <h2 style={{ fontSize: '1.15rem', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            Action Items 
            <span className="badge badge-neutral" style={{ fontSize: '0.8rem' }}>{filteredItems.length}</span>
          </h2>
          <div style={{ display: 'flex', gap: 12 }}>
            <select className="form-select" style={{ minWidth: 180, fontSize: '0.85rem' }} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="All Pending">All Pending</option>
              <option value="All">All Items</option>
              <option value="Risk">Risks</option>
              <option value="Control">Controls</option>
              <option value="Vulnerability">Vulnerabilities</option>
              <option value="Documents">Document Reviews</option>
            </select>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--bg-base)', borderBottom: '1px solid var(--border)' }}>
                <th onClick={() => requestSort('_typeAndId')} style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>Type & ID {renderSortIcon('_typeAndId')}</th>
                <th onClick={() => requestSort('_title')} style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>Description {renderSortIcon('_title')}</th>
                <th onClick={() => requestSort('_priority')} style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>Details {renderSortIcon('_priority')}</th>
                <th onClick={() => requestSort('_dueDate')} style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>Due Date {renderSortIcon('_dueDate')}</th>
                <th onClick={() => requestSort('_status')} style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>Status {renderSortIcon('_status')}</th>
                <th style={{ padding: '14px 20px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredItems.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No items matching your filter.</td>
                </tr>
              ) : sortedFilteredItems.map(item => {
                const isOver = item._dueDate && isOverdue(item._dueDate) && item._sortWeight === 2;
                return (
                  <tr key={`${item._type}-${item._id}`} style={{ borderBottom: '1px solid var(--border)', background: isOver ? 'var(--danger-dim, rgba(239, 68, 68, 0.04))' : 'transparent', transition: 'background 0.2s' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{item._type}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--accent)' }}>{item._id}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px', maxWidth: 350 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }} title={item._title}>
                        {item._title}
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <span className={`badge ${item._priorityBadge}`} style={{ fontSize: '0.7rem' }}>{item._priority}</span>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: isOver ? 'var(--danger)' : 'var(--text-secondary)' }}>
                        <Clock size={14} />
                        <span style={{ fontSize: '0.85rem', fontWeight: isOver ? 600 : 400 }}>{safeFormat(item._dueDate)}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <span className={`badge ${item._statusBadge}`}>{item._status}</span>
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        {item._type === 'Document Review' ? (
                          <button className="btn btn-primary btn-sm" onClick={() => navigate('/review-center')}>Review</button>
                        ) : item._type === 'Vulnerability' ? (
                          <>
                            <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewItem({ type: 'vulnerability', data: item._raw })}><Eye size={15} /></button>
                            <button className="btn btn-ghost btn-sm" title="Edit in Module" onClick={() => navigate('/vulnerabilities')}><ExternalLink size={15} /></button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewItem({ type: item._type.toLowerCase(), data: item._raw })}><Eye size={15} /></button>
                            <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setEditItem({ type: item._type.toLowerCase(), data: item._raw })}><Edit size={15} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      {viewItem && <ViewModal item={viewItem} onClose={() => setViewItem(null)} onEdit={() => { setEditItem(viewItem); setViewItem(null); }} navigate={navigate} users={users} />}
      {editItem && editItem.type === 'risk' && (
        <RiskModal 
          riskId={editItem.data.id} 
          onClose={() => setEditItem(null)} 
          onSave={d => updateRisk.mutate({ id: editItem.data.id, data: d })} 
          isSaving={updateRisk.isPending} 
          assets={assets} 
          securityControls={securityControls} 
          user={user}
        />
      )}
      {editItem && editItem.type === 'control' && (
        <FullEditModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSave={(id, payload) => updateCtrl.mutate({ id, data: payload })}
          isSaving={updateCtrl.isPending}
          user={user}
        />
      )}
    </div>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ item, onClose, onEdit, navigate, users }) {
  const isRisk = item.type === 'risk';
  const isVuln = item.type === 'vulnerability';
  const d = item.data;
  
  const getOwnerNames = (owner_ids) => {
    try {
      const ids = Array.isArray(owner_ids) ? owner_ids : JSON.parse(owner_ids || '[]');
      if (!ids.length) return 'None';
      return ids.map(id => users?.find(u => u.id === id)?.full_name || 'Unknown').join(', ');
    } catch {
      return 'None';
    }
  };

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ minWidth: 140, fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>View {isRisk ? 'Risk' : isVuln ? 'Vulnerability' : 'Control'}: {isRisk || isVuln ? d.custom_id : d.control_id}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {isRisk ? (
            <>
              <Row label="Description" value={d.description} />
              <Row label="Status" value={d.status} />
              <Row label="Risk Score" value={d.risk_score} />
              <Row label="Owners" value={getOwnerNames(d.owner_ids)} />
              <Row label="Likelihood" value={d.likelihood} />
              <Row label="Impact" value={d.impact} />
              <Row label="Treatment Type" value={d.treatment_type} />
              <Row label="Treatment Plan" value={d.treatment_plan} />
              <Row label="Due Date" value={safeFormat(d.due_date)} />
              <Row label="Assigned Date" value={safeFormat(d.assigned_date)} />
              <Row label="Comments" value={d.comments} />
            </>
          ) : isVuln ? (
            <>
              <Row label="Name" value={d.name} />
              <Row label="Description" value={d.description} />
              <Row label="Status" value={d.status} />
              <Row label="Owners" value={getOwnerNames(d.owner_ids)} />
              <Row label="Severity" value={d.severity} />
              <Row label="Due Date" value={safeFormat(d.due_date)} />
              <Row label="Mapped Risks" value={d.risk_ids && d.risk_ids !== '[]' ? JSON.parse(d.risk_ids).length + ' risk(s)' : 'None'} />
            </>
          ) : (
            <>
              <Row label="Control Name" value={d.name} />
              <Row label="Description" value={d.description} />
              <Row label="Status" value={d.implementation_status} />
              <Row label="Owners" value={getOwnerNames(d.owner_ids)} />
              <Row label="Type" value={d.control_type} />
              <Row label="Category" value={d.control_category} />
              <Row label="Annex A Mapping" value={d.annex_control_id} />
              <Row label="Effectiveness" value={d.effectiveness} />
              <Row label="Due Date" value={safeFormat(d.due_date)} />
              <Row label="Assigned Date" value={safeFormat(d.assigned_date)} />
              <Row label="Notes" value={d.notes} />
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {!isVuln ? (
            <button className="btn btn-primary" onClick={onEdit}><Edit size={14} /> Edit</button>
          ) : (
            <button className="btn btn-primary" onClick={() => navigate('/vulnerabilities')}><ExternalLink size={14} /> Edit in Module</button>
          )}
        </div>
      </div>
    </div>
  );
}

function FullEditModal({ item, onClose, onSave, isSaving, user }) {
  const src = item.data;

  // Control form state
  const [cForm, setCForm] = useState({
    control_id: src.control_id || '',
    name: src.name || '',
    description: src.description || '',
    owner_ids: src.owner_ids ? (typeof src.owner_ids === 'string' ? JSON.parse(src.owner_ids) : src.owner_ids) : [],
    control_type: src.control_type || 'Preventive',
    control_category: src.control_category || 'Technical',
    implementation_status: src.implementation_status || 'Not Started',
    effectiveness: src.effectiveness || 3,
    annex_mapping: src.annex_control_id 
      ? src.annex_control_id.split(',').map(id => id.trim()).filter(Boolean) 
      : [],
    due_date: normalizeDate(src.due_date),
    notes: src.notes || '',
  });

  const setC = (k, v) => setCForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    onSave(src.id, {
      ...cForm,
      effectiveness: Number(cForm.effectiveness),
      annex_control_id: cForm.annex_mapping,
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <h2>Edit Control: {src.control_id}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Control Name *</label>
                  <input className="form-input" value={cForm.name} onChange={e => setC('name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Annex A Mapping</label>
                  <select 
                    className="form-select" 
                    value="" 
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) return;
                      setCForm(f => {
                        const current = f.annex_mapping || [];
                        const next = current.includes(val)
                          ? current.filter(id => id !== val)
                          : [...current, val];
                        return { ...f, annex_mapping: next };
                      });
                    }}
                  >
                    <option value="" disabled>Map Annex A controls...</option>
                    {ALL_ANNEX.map(a => (
                      <option key={a.id} value={a.id}>
                        {cForm.annex_mapping?.includes(a.id) ? '✓ ' : ''} {a.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {cForm.annex_mapping?.map(id => {
                      const a = ALL_ANNEX.find(ctrl => ctrl.id === id);
                      return a ? (
                        <span key={id} className="badge badge-info" style={{ fontSize: '0.6875rem', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {a.label}
                          <X size={12} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => setCForm(f => ({ ...f, annex_mapping: (f.annex_mapping || []).filter(currId => currId !== id) }))} />
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" style={{ minHeight: 60 }} value={cForm.description} onChange={e => setC('description', e.target.value)} />
              </div>
              <div className="grid-3">
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-select" value={cForm.control_type} onChange={e => setC('control_type', e.target.value)}>
                    {CONTROL_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={cForm.control_category} onChange={e => setC('control_category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Owner</label>
                  <MultiUserSelect values={cForm.owner_ids || []} onChange={v => setC('owner_ids', v)} disabled={user?.role === 'Contributor'} />
                </div>
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Implementation Status</label>
                  <select className="form-select" value={cForm.implementation_status} onChange={e => setC('implementation_status', e.target.value)}>
                    {CTRL_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-input" value={cForm.due_date} onChange={e => setC('due_date', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Effectiveness (1–5)</label>
                <input type="number" className="form-input" min={1} max={5} value={cForm.effectiveness} onChange={e => setC('effectiveness', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Implementation Notes</label>
                <textarea className="form-textarea" style={{ minHeight: 72 }} value={cForm.notes} onChange={e => setC('notes', e.target.value)} />
              </div>
            </>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={isSaving} onClick={handleSave}>
            <Save size={14} /> {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
